import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IncomeAttributeState } from "../../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { getLookups } from "../../../redux/actions/lookups/lookups";
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from "../../../redux/actions/incomeAttributes/incomeAttributes";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import Total from "../../../components/common/Total/Total";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem';
import IncomeAttributeLookupObject from "../../../types/lookups/IncomeAttributeLookupObject";
import { useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { useNavigation } from "../../../utils/context/NavigationContext";
import ErrorMessages from "../../../components/common/ErrorMessages/ErrorMessages";
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../../../utils/previousIncomeAttributes";
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from "../../../redux/actionTypes/actionsTypes";
import MotelRevenueTab from "./MotelRevenueTab";
import PreviousCampaignIncomeAttributes from "../../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes";

export default function MotelRevenue() {
  const { t } = useTranslation(['common', 'pie']);
  const dispatch = useDispatch();
  const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
  const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes);

  const lookupState = useSelector(selectLookups);
  const menuState = useSelector(selectMenu);
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const saveData: SaveDataState = useSelector(selectSaveData);
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
  const tabList = menuState.activeItem.subItems;
  const [totalMotelRevenue, setTotalMotelRevenue] = useState(0);
  const { direction } = useNavigation()
  const { errors, clearErrors } = useFormErrorContext();
  const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
  const currentCampaignYear = getCurrentCampaignYear()

  // calculated variables for keeping code clean
  const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
  const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string);

  // This hook sends a request to fetch income attribute data if the following conditions are met:
  // 1. Return type is import
  // 2. A request to fetch wasn't alreadt sent
  // 3. If system has a failed response and/or result of a different snapshot stored
  const getIncomeAttributes = () => {
    if ((!incomeAttributesState.loading && !incomeAttributesState.success)
      || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem('currentSnapshotId') as string)) {
      dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem('currentSnapshotId') as string));
    }

    const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

    if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
      dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
    }
  }


  useEffect(() => {
    getIncomeAttributes()
    // eslint-disable-next-line
  }, []);

  // This hook fetches income attribute lookups if they are not already loaded in system
  useEffect(() => {
    if (!lookupState.success) {
      dispatch(getLookups(lookupType));
    }
  }, [dispatch, lookupState.success, lookupType]);

  // If table is valid, this hook sends a save request and handles response
  useEffect(() => {
    if (saveData.saving) {
      clearErrors()
      const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
      const triggerSpectral = isInternal || direction !== "backward"
      dispatch(putIncomeAttributesBySnapshotId(sessionStorage.getItem("currentSnapshotId") as string, {
        "attributes": incomeAttributes,
        "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
        "trigger_spectral": triggerSpectral,
        "is_internal": isInternal
      }));
    }
  }, [dispatch, incomeAttributesState.data, menuState.activeItem.name, saveData.saving, profileOverview.profile.profile_type_code, direction, clearErrors, isInternal]);

  useEffect(() => {
    if (saveResultState.status === 200) {
      dispatch(getIncomeAttributesBySnapshotId(currentSnapshotId));
    }
  }, [dispatch, currentSnapshotId, saveResultState]);

  const calculateTotal = useCallback((year: string, previousYearTotal: boolean) => {
    const findAllPreviousAttributes = (year: string) => {
      const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
      if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
        let previousSnapshotData = { incomeAttribute: [] }
        previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
        if (previousSnapshotData.incomeAttribute) {
          return previousSnapshotData.incomeAttribute;
        }
      }
      return null;
    }

    let total = 0;
    let filteredAttributes: Record<string, unknown>[] = [];


    let data = null
    if (previousYearTotal) {
      data = findAllPreviousAttributes(year)
    } else {
      data = incomeAttributesState.data
    }

    if (data) {
      filteredAttributes = (data as Record<string, unknown>[]).filter(row => {
        return (lookupState.lookups[1]["motelRevenue"] as IncomeAttributeLookupObject[] ?? []).some(f => {
          return f.incomeAttributeCode === row['code']
        })
      });

      filteredAttributes.forEach(record => {
        if (record['value']) {
          total += record['value'] as number
        }
      });
    }
    return total;
  }, [incomeAttributesState.data, lookupState, isInternal]);

  // This hook calculates Total Motel Revenue by tracking changes to income attribute data
  useEffect(() => {
    if (!incomeAttributesState.loading && incomeAttributesState.data && lookupState.success) {
      const total = calculateTotal(String(currentCampaignYear), false);
      setTotalMotelRevenue(total);
    }
  }, [incomeAttributesState, calculateTotal, lookupState.success, currentCampaignYear]);

  // This functions handles changes made to income attribute data columns
  const handleIncomeAttrChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const row = (incomeAttributesState.data as Record<string, unknown>[]).find(row => row.code === code);
      const na = (newAmt === undefined || newAmt === null) ? null : newAmt
      if (row) {
        row['value'] = newAmt;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: currentSnapshotId,
          code: code,
          description: null,
          value: na,
          type: "1",
          percent: null,
          year: null,
        });
      }

      // Update totals
      const newTotal = calculateTotal(String(currentCampaignYear), false);
      setTotalMotelRevenue(newTotal);
    }
  }

  const handleCommentChange = (value: string, name: string) => {
    const idx = name.lastIndexOf("-");
    if (idx !== -1) {
      const code = name.substring(idx + 1);
      const incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(row => row.code === code);
      const v = value.trim() === "" ? null : value.trim();
      if (incomeAttributesData) {
        incomeAttributesData["description"] = v;
      } else {
        (incomeAttributesState.data as Record<string, unknown>[]).push({
          snapshotId: currentSnapshotId,
          code: code,
          description: v,
          value: null,
          type: "1",
          percent: null,
          year: null,
        });
      }
    }
  }

  return (<>
    <div className="full-width">
      <div className="content-container">
        <ErrorMessages errors={errors} />
        <p className="strong">{t('revenues.caption', { ns: 'pie' })}</p>
        {(!incomeAttributesState.loading && !previousIncomeAttributesState.loading && !lookupState.loading && tabList) &&
          <>
            <div className="accordion-root">
              <div className={accordionDisplay(isInternal, "accordion")}>
                <ControlledAccordion providerValue={providerValue}>
                  {tabList.map(tab => {
                    const tabLookupKey = tab.name === "nonOperatingIncomeRevenues" ? "nonOperatingIncomeRevenues" : "motelRevenue"
                    return <AccordionItem
                      header={t(tab.name, { ns: 'navigation' })}
                      key={tab.name}
                      itemKey={tab.name}
                    >
                      <MotelRevenueTab
                        lookups={lookupState.lookups[1] ? (lookupState.lookups[1] as Record<string, IncomeAttributeLookupObject[]>)[tabLookupKey] : []}
                        valueChangeHandler={handleIncomeAttrChange}
                        handleCommentChange={handleCommentChange}
                        incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                      />
                    </AccordionItem>
                  })}
                </ControlledAccordion>
              </div>
              {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent="mtlRevenue" tabList={tabList} year={String(getCurrentCampaignYear() - 1)} /> : <></>}
              {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent="mtlRevenue" tabList={tabList} year={String(getCurrentCampaignYear() - 2)} /> : <></>}
            </div>
          </>}
        <div className='total-container'>
          <div className="accordion-root">
            <div className={accordionDisplay(isInternal, "accordion")}>
              <Total text={t('revenues.totalMotelRevenue', { ns: 'pie' })} value={totalMotelRevenue} />
            </div>
            {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
              <Total text={t('revenues.totalMotelRevenue', { ns: 'pie' })} value={calculateTotal(String(currentCampaignYear - 1), true)} />
            </div> : <></>}
            {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
              <Total text={t('revenues.totalMotelRevenue', { ns: 'pie' })} value={calculateTotal(String(currentCampaignYear - 2), true)} />
            </div> : <></>}
          </div>
        </div>
      </div>
    </div>
  </>);
}
